(function (global) {
  'use strict';
  const revision = 'three-view-shopify-controller-v1.6-mixed-roses';
  const instances = new Set(), scriptLoads = new Map();
  const clone = value => JSON.parse(JSON.stringify(value));
  const abortError = () => Object.assign(new Error('Aborted'), { name: 'AbortError' });
  const visible = node => Boolean(node && !node.closest('[hidden]') && node.getClientRects().length && global.getComputedStyle(node).visibility !== 'hidden');
  const requiredResources = ['photographicLeafTexture', 'petalSurfaceTexture', 'satinSurfaceTexture', 'bakedFoliageOcclusion'];

  function snapshot(state) {
    if (!state || !Array.isArray(state.stabilized) || !Array.isArray(state.fresh) || 'addons' in state || state.stabilized.length + state.fresh.length > 9) throw new Error('Invalid graphical selection');
    const items = [...state.stabilized, ...state.fresh];
    for (const item of items) {
      if (item.roseColors === undefined) continue;
      const type = item.type || item.id, count = Number(item.quantity);
      const allowed = type === 'bow-roses' ? [1, 3] : item.roseSize === 'piccola' ? [5, 8, 10] : [3, 5, 8];
      const resolved = entry => entry && typeof entry.colour_id === 'string' && entry.colour_id && typeof entry.label === 'string' && entry.label && /^#[\da-f]{3}([\da-f]{3})?$/i.test(entry.color || '');
      if (!['roses', 'bow-roses'].includes(type) || !allowed.includes(count) || !Array.isArray(item.roseColors) || item.roseColors.length !== count || !item.roseColors.every(resolved) || !/^#[\da-f]{3}([\da-f]{3})?$/i.test(item.color || '') || items.filter(value => (value.type || value.id) === type).length !== 1) throw new Error('Invalid ordered rose colours');
    }
    return clone(state);
  }

  function runtimeEntry(manifest) {
    const entry = manifest.runtime || manifest.resources?.['assets/cl-three-view-runtime-v1.js'];
    if (!entry || !/^[a-f0-9]{64}$/.test(entry.sha256 || '')) throw new Error('Missing signed runtime');
    const url = new URL(entry.url, global.location.href);
    if (url.username || url.password || !((url.origin === global.location.origin && ['http:', 'https:'].includes(url.protocol)) || (url.protocol === 'https:' && url.hostname === 'cdn.shopify.com'))) throw new Error('Untrusted runtime origin');
    return { url: url.href, sha256: entry.sha256 };
  }

  function loadRuntime(manifest) {
    const entry = runtimeEntry(manifest), key = entry.url + '#' + entry.sha256;
    if (scriptLoads.has(key)) return scriptLoads.get(key);
    const promise = new Promise((resolve, reject) => {
      const script = global.document.createElement('script');
      const cleanup = () => { clearTimeout(timer); script.onload = script.onerror = null; script.remove(); };
      const timer = setTimeout(() => { cleanup(); reject(new Error('Runtime loading timed out')); }, 25000);
      script.onload = () => { cleanup(); global.CLThreeViewRuntimeV1 ? resolve(global.CLThreeViewRuntimeV1) : reject(new Error('Runtime unavailable')); };
      script.onerror = () => { cleanup(); reject(new Error('Runtime blocked or unavailable')); };
      script.crossOrigin = 'anonymous';
      script.integrity = 'sha256-' + global.btoa(entry.sha256.match(/../g).map(byte => String.fromCharCode(parseInt(byte, 16))).join(''));
      script.src = entry.url; global.document.head.appendChild(script);
    });
    scriptLoads.set(key, promise);
    promise.catch(() => { if (scriptLoads.get(key) === promise) scriptLoads.delete(key); });
    return promise;
  }

  class ThreeView {
    constructor(root, options = {}) {
      this.root = root; this.options = options;
      this.manifest = options.manifest || JSON.parse(root.querySelector('[data-cl-three-manifest]')?.textContent || 'null');
      if (!this.manifest || this.manifest.version !== 1) throw new Error('Invalid three-view manifest');
      this.labels = this.manifest.labels || {};
      this.sideRoot = options.sideRoot || root.closest('[data-clside-root]');
      this.side = this.sideRoot?.clsideInstance;
      this.host = root.closest('cl-configurator');
      this.stateHub = options.stateHub || (typeof this.sideRoot?.cl3dGetState === 'function' ? this.sideRoot : this.side?.stateHub) || this.host?.querySelector('[data-cl3d-root]') || this.sideRoot;
      let selectedTop = null;
      try { if (this.sideRoot?.dataset.clsideTwoDSelector) selectedTop = this.host?.querySelector(this.sideRoot.dataset.clsideTwoDSelector) || global.document.querySelector(this.sideRoot.dataset.clsideTwoDSelector); } catch (_) { selectedTop = null; }
      this.top = options.topPanel || this.side?.topStage || selectedTop || this.host?.querySelector('[data-clc-stage]');
      this.sidePanel = this.side?.panel || this.sideRoot?.querySelector('[data-clside-panel]');
      this.panel = root.querySelector('[data-cl-three-panel]');
      this.canvas = root.querySelector('[data-cl-three-canvas]');
      this.status = root.querySelector('[data-cl-three-status]');
      this.errorNode = root.querySelector('[data-cl-three-error]');
      this.thirdTab = this.sideRoot?.querySelector('[data-cl-three-tab]') || root.querySelector('[data-cl-three-tab]');
      if (!this.sideRoot || !this.stateHub || !this.top || !this.panel || !this.canvas || !this.thirdTab) throw new Error('Incomplete three-view markup');
      this.tabs = [...this.sideRoot.querySelectorAll('[data-clside-view], [data-cl-three-tab]')];
      this.view = this.side?.mode === 'side' ? 'side' : 'top'; this.previousPhotoView = this.view;
      this.desired = null; this.rendered = null; this.version = 0; this.appliedVersion = -1;
      this.engine = null; this.prepared = null; this.preflight = null; this.boot = null; this.generation = 0;
      this.runId = 0; this.expansionEpoch = 0; this.expanding = false; this.expansionFocus = null;
      this.disposed = false; this.error = null; this.compositionPending = false; this.rebuildRequired = false; this.running = false; this.timer = 0; this.cancelEpoch = 0; this.waiters = []; this.listeners = [];
      this.listen(this.stateHub, 'cl3d:update', event => { try { this.setState(event.detail?.state); } catch (error) { this.fail(error); } });
      this.listen(this.sideRoot, 'click', event => this.click(event));
      this.listen(this.sideRoot, 'keydown', event => this.tabKey(event), true);
      this.listen(this.sideRoot, 'clside:unavailable', event => {
        event.preventDefault();
        if (this.view !== 'side') return;
        this.setView('top').catch(() => {});
        this.tabs.find(tab => tab.dataset.clsideView === 'top')?.focus();
      });
      this.listen(this.canvas, 'keydown', event => this.canvasKey(event));
      this.listen(this.panel, 'keydown', event => this.panelKey(event));
      this.listen(this.canvas, 'webglcontextlost', event => { event.preventDefault(); this.fail(new Error('WebGL context lost')); });
      this.listen(global.document, 'visibilitychange', () => { if (global.document.hidden) this.suspendWork(); this.syncActive(); if (this.active()) this.schedule(); });
      this.listen(global.document, 'fullscreenchange', () => this.expansionState());
      this.listen(global, 'resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => { if (this.disposed) return; if (this.view === 'side') this.photo('side'); else if (this.view === '3d') this.engine?.resize(); }, 80);
      });
      this.panel.hidden = true; this.readState(); instances.add(this); root.clThreeView = this;
    }
    listen(target, type, handler, options) { target.addEventListener(type, handler, options); this.listeners.push(() => target.removeEventListener(type, handler, options)); }
    active() { return this.view === '3d' && !global.document.hidden && !this.disposed; }
    label(name) { return this.labels[name] || ''; }
    stateStatus(name, busy = false) {
      this.root.dataset.clThreeStatus = name; this.panel.setAttribute('aria-busy', String(busy));
      if (name !== 'error') delete this.root.dataset.clThreeDiagnostic;
      if (this.status) this.status.textContent = this.label(name);
      if (this.errorNode) { this.errorNode.hidden = name !== 'error' && name !== 'composition_pending'; this.errorNode.textContent = this.errorNode.hidden ? '' : this.label(name); }
    }
    readState() {
      const hub = typeof this.stateHub.cl3dGetState === 'function' ? this.stateHub : typeof this.sideRoot.cl3dGetState === 'function' ? this.sideRoot : null;
      if (hub) { try { this.setState(hub.cl3dGetState()); } catch (error) { this.fail(error); } }
    }
    loadingProgress(progress, generation = this.generation) {
      if (this.disposed || generation !== this.generation || this.error || this.compositionPending || !this.active() || !this.status) return;
      let message = '';
      if (progress.phase === 'resources' && Number.isInteger(progress.completed) && Number.isInteger(progress.total) && progress.total > 0 && progress.completed >= 0 && progress.completed <= progress.total) {
        message = this.label('loading_resources').replace('__DONE__', String(progress.completed)).replace('__TOTAL__', String(progress.total));
      } else if (progress.phase === 'renderer') message = this.label('loading_renderer');
      else if (progress.phase === 'geometry') message = this.label('loading_geometry');
      if (message) this.status.textContent = message;
    }
    setState(state) {
      const next = snapshot(state);
      if (this.disposed) return;
      if (JSON.stringify(next) === JSON.stringify(this.desired)) return;
      this.desired = next; this.version++; this.cancelEpoch++;
      if (this.error && !this.compositionPending) return;
      this.error = null;
      this.engine?.cancelPending();
      const selection = (this.prepared || this.preflight)?.classify?.(next);
      if (selection?.route === 'pending') {
        this.pendingComposition(selection);return;
      }
      // Keep an old mesh hidden until the replacement is actually acknowledged.
      const wasPending = this.compositionPending;this.compositionPending = false;
      if (wasPending && this.active()) this.stateStatus('updating', true);
      if (this.active()) this.schedule();
    }
    syncActive() { this.engine?.setActive(this.active() && !this.error && !this.compositionPending && this.root.dataset.clThreeStale !== 'true'); if (!this.active() || this.compositionPending) this.engine?.setAutoRotate(false); }
    suspendWork() {
      this.cancelEpoch++; this.runId++; this.running = false;
      clearTimeout(this.timer); this.timer = 0;
      if (this.boot) {
        this.generation++; this.abortController?.abort(); this.boot = null;
        this.engine?.dispose(); this.prepared?.dispose(); this.preflight?.dispose(); this.engine = null; this.prepared = null; this.preflight = null;
        this.rendered = null; this.appliedVersion = -1;
      } else this.engine?.cancelPending();
    }
    pendingComposition(selection) {
      this.compositionPending = true;
      this.error = selection.message || this.label('composition_pending');
      this.root.dataset.clThreeStale = 'true';
      if (this.boot && !this.engine && this.preflight) {
        this.generation++; this.runId++; this.running = false;
        this.abortController?.abort(); this.boot = null;
        this.prepared?.dispose(); this.prepared = null;
      }
      this.engine?.cancelPending();this.syncActive();this.stateStatus('composition_pending');
      this.collapse();
      const error = Object.assign(new Error(this.error), { code: 'CL_THREE_COMPOSITION_PENDING', publicMessage: this.error });
      this.settle(error);
    }
    photo(view) {
      this.side = this.sideRoot.clsideInstance || this.side;
      if (this.side) {
        if (view === 'side') this.side.decorationSignature = '';
        this.side.switchView(view, false);
      } else { this.top.hidden = view === 'side'; if (this.sidePanel) this.sidePanel.hidden = view !== 'side'; }
    }
    setView(view) {
      if (!['top', 'side', '3d'].includes(view) || this.disposed) return Promise.reject(new Error('Invalid view'));
      if (view === 'side' && this.tabs.find(tab => tab.dataset.clsideView === 'side')?.disabled) view = 'top';
      if (this.view !== view) this.cancelEpoch++;
      this.view = view; this.panel.hidden = view !== '3d';
      if (view === '3d') { this.photo('top'); this.top.hidden = true; if (this.sidePanel) this.sidePanel.hidden = true; }
      else { this.previousPhotoView = view; this.suspendWork(); this.photo(view); this.collapse(); }
      // A synchronously unavailable photograph can select the safe panel from
      // inside photo(). Do not overwrite that newer selection with old tabs.
      if (this.view !== view) return Promise.resolve(this.getDiagnostics());
      for (const tab of this.tabs) {
        const selected = tab === this.thirdTab ? view === '3d' : tab.dataset.clsideView === view;
        tab.setAttribute('aria-selected', String(selected)); tab.setAttribute('tabindex', selected ? '0' : '-1');
      }
      this.syncActive();
      if (view !== '3d') { this.settle(abortError()); return Promise.resolve(this.getDiagnostics()); }
      if (!this.desired) this.readState();
      if (!this.error) {
        const current = this.rendered && this.appliedVersion === this.version;
        this.stateStatus(current ? 'ready' : this.rendered ? 'updating' : 'loading', !current);
      }
      this.engine?.resize(); this.schedule();
      return this.whenIdle();
    }
    schedule() {
      if (this.disposed || this.error || !this.active() || this.running || this.timer) return;
      if (!this.desired) { this.stateStatus('waiting'); return; }
      this.timer = setTimeout(() => { this.timer = 0; this.drain(); }, 45);
    }
    async ensureEngine() {
      if (this.engine && !this.error && !this.rebuildRequired) return this.engine;
      if (this.boot) return this.boot;
      this.engine?.dispose(); this.engine = null;
      if (this.rebuildRequired) { this.prepared?.dispose(); this.preflight?.dispose(); this.prepared = null; this.preflight = null; }
      this.rebuildRequired = false;
      const generation = ++this.generation;
      this.abortController = new AbortController();
      this.stateStatus('loading', true);
      const work = (async () => {
        let prepared = this.prepared;
        if (!prepared) {
          const runtime = this.options.loadRuntime ? await this.options.loadRuntime(this.manifest) : await loadRuntime(this.manifest);
          if (this.disposed || generation !== this.generation) throw abortError();
          if (runtime.preflight && !this.preflight) {
            const preflight = await runtime.preflight(this.manifest, { signal: this.abortController.signal });
            if (this.disposed || generation !== this.generation) { preflight.dispose(); throw abortError(); }
            this.preflight = preflight;
          }
          if (this.preflight?.classify?.(this.desired)?.route === 'pending') return null;
          let context = null;
          try {
            context = this.canvas.getContext('webgl', { alpha: false, antialias: true, depth: true, premultipliedAlpha: false, powerPreference: 'default', preserveDrawingBuffer: false }) || this.canvas.getContext('experimental-webgl', { alpha: false, antialias: true, depth: true });
          } catch (_) {}
          if (!context) throw Object.assign(new Error('WebGL non disponibile su questo dispositivo.'), { code: 'CL_THREE_WEBGL_UNAVAILABLE' });
          prepared = await runtime.prepare(this.manifest, { signal: this.abortController.signal, preflight: this.preflight, onProgress: progress => this.loadingProgress(progress, generation) });
          if (this.disposed || generation !== this.generation) { prepared.dispose(); throw abortError(); }
          this.prepared = prepared;
        }
        // Keep the verified preparation, but defer the renderer/Worker until a
        // supported selection needs it. Check the latest state after loading.
        if (prepared.classify?.(this.desired)?.route === 'pending') return null;
        const engine = prepared.create(this.canvas, { onError: error => { if (!this.disposed && generation === this.generation) this.fail(error); } });
        if (this.disposed || generation !== this.generation) { engine.dispose(); throw abortError(); }
        this.engine = engine;
        await engine.ready;
        if (this.disposed || generation !== this.generation) throw abortError();
        const diagnostic = engine.getDiagnostics();
        if (diagnostic.error || diagnostic.contextLost || requiredResources.some(key => diagnostic[key] !== true)) throw new Error('3D materials or context unavailable');
        engine.setView('front'); this.error = null; this.rebuildRequired = false; this.rendered = null; this.appliedVersion = -1; this.syncActive();
        return engine;
      })();
      this.boot = work;
      try { return await work; }
      finally { if (this.boot === work) this.boot = null; }
    }
    async drain() {
      if (this.running || this.error || !this.active() || !this.desired) return;
      this.running = true;
      const runId = ++this.runId;
      let epoch = this.cancelEpoch;
      try {
        const initialSelection = (this.prepared || this.preflight)?.classify?.(this.desired);
        if (initialSelection?.route === 'pending') { this.pendingComposition(initialSelection);return; }
        const engine = await this.ensureEngine();
        if (!engine) {
          // ensureEngine has released its boot lock before feedback settles
          // callers, so a photo return can retain the completed preparation.
          const selection = (this.prepared || this.preflight)?.classify?.(this.desired);
          if (selection?.route === 'pending') this.pendingComposition(selection);
          return;
        }
        while (runId === this.runId && this.active() && this.appliedVersion !== this.version) {
          const version = this.version, state = snapshot(this.desired); epoch = this.cancelEpoch;
          const selection = this.prepared?.classify?.(state);
          if (selection?.route === 'pending') { this.pendingComposition(selection);break; }
          this.stateStatus(this.rendered ? 'updating' : 'loading', true);
          this.loadingProgress({ phase: 'geometry' });
          await engine.setState(state);
          if (runId !== this.runId || !this.active()) break;
          const diagnostic = engine.getDiagnostics();
          if (diagnostic.error || diagnostic.contextLost) throw new Error(diagnostic.error || 'WebGL context lost');
          if (version !== this.version) continue;
          const previousFrames = diagnostic.renderFrames;
          if (!Number.isFinite(previousFrames)) throw new Error('3D frame diagnostics unavailable');
          this.error = null;this.compositionPending = false;
          delete this.root.dataset.clThreeStale;this.syncActive();
          engine.resize();
          await this.confirmFrame(engine, previousFrames, version, epoch);
          if (runId !== this.runId) break;
          this.rendered = state; this.appliedVersion = version;
          this.stateStatus('ready'); this.settle();
          this.root.dispatchEvent(new CustomEvent('cl-three-view:rendered', { detail: this.getDiagnostics() }));
        }
      } catch (error) {
        if (!this.disposed && runId === this.runId && (error.name !== 'AbortError' || this.active() && epoch === this.cancelEpoch && !this.compositionPending)) this.fail(error);
      }
      finally { if (runId === this.runId) { this.running = false; if (this.active() && !this.error && this.appliedVersion !== this.version) this.schedule(); } }
    }
    async confirmFrame(engine, previousFrames, version, epoch) {
      const deadline = Date.now() + (this.options.frameTimeoutMs || 10000);
      while (true) {
        if (this.disposed || !this.active() || version !== this.version || epoch !== this.cancelEpoch) throw abortError();
        const diagnostic = engine.getDiagnostics();
        if (diagnostic.error || diagnostic.contextLost) throw new Error(diagnostic.error || 'WebGL context lost');
        if (diagnostic.renderFrames > previousFrames) return;
        if (Date.now() >= deadline) throw Object.assign(new Error('3D first frame timed out'), { code: 'CL_THREE_FRAME_TIMEOUT' });
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    whenIdle() {
      if (this.disposed) return Promise.reject(abortError());
      if (this.error) return Promise.reject(Object.assign(new Error(this.error), this.compositionPending ? { code: 'CL_THREE_COMPOSITION_PENDING' } : {}));
      if (this.appliedVersion === this.version && this.rendered) return Promise.resolve(this.getDiagnostics());
      return new Promise((resolve, reject) => { this.waiters.push({ resolve, reject }); this.schedule(); });
    }
    settle(error) { const waiters = this.waiters.splice(0); for (const waiter of waiters) error ? waiter.reject(error) : waiter.resolve(this.getDiagnostics()); }
    fail(error) {
      if (this.disposed) return;
      if (error?.code === 'CL_THREE_COMPOSITION_PENDING') { this.pendingComposition({message:error.publicMessage || error.message});return; }
      this.error = String(error?.message || error);
      this.compositionPending = false;
      this.cancelEpoch++; this.runId++; this.generation++; this.running = false;
      clearTimeout(this.timer); this.timer = 0; this.abortController?.abort(); this.boot = null;
      this.root.dataset.clThreeDiagnostic = this.error;
      // Keep operational diagnostics observable even when DOM inspection is unavailable.
      // Fixed categories only: never log selections, personal input or signed URLs.
      const errorKind = /webgl/i.test(this.error) ? 'webgl-unavailable' : /SHA256|signed resource/i.test(this.error) ? 'resource-integrity' : /timed out|timeout/i.test(this.error) ? 'timeout' : /resource|fetch|network/i.test(this.error) ? 'resource-unavailable' : 'renderer-error';
      global.console?.warn?.('[Corone Laureae 3D] ' + errorKind);
      const diagnostic = this.engine?.getDiagnostics();
      if (!diagnostic || /WebGL context lost/i.test(this.error) || diagnostic.contextLost || diagnostic.error || requiredResources.some(key => diagnostic[key] !== true)) this.rebuildRequired = true;
      this.engine?.cancelPending(); this.syncActive();
      this.stateStatus('error');
      this.collapse();
      this.engine?.dispose(); this.prepared?.dispose(); this.preflight?.dispose(); this.engine = null; this.prepared = null; this.preflight = null; this.rebuildRequired = true;
      if (errorKind === 'webgl-unavailable' && this.label('error_webgl')) {
        if (this.status) this.status.textContent = this.label('error_webgl');
        if (this.errorNode) this.errorNode.textContent = this.label('error_webgl');
      }
      this.settle(error instanceof Error ? error : new Error(this.error));
    }
    retry() {
      if (this.disposed) return Promise.reject(abortError());
      if (this.compositionPending) return this.whenIdle();
      this.cancelEpoch++; this.runId++; this.generation++; this.running = false;
      clearTimeout(this.timer); this.timer = 0; this.abortController?.abort(); this.engine?.dispose(); this.prepared?.dispose(); this.preflight?.dispose();
      this.engine = null; this.prepared = null; this.preflight = null; this.boot = null; this.error = null; this.appliedVersion = -1; this.rendered = null;
      this.schedule(); return this.whenIdle();
    }
    async expand() {
      if (this.disposed || this.view !== '3d') return;
      if (this.expanding || this.panel.classList.contains('cl-three-view--expanded') || global.document.fullscreenElement === this.panel) return this.collapse();
      const epoch = ++this.expansionEpoch;
      this.expanding = true; this.expansionFocus = global.document.activeElement;
      try { if (!this.panel.requestFullscreen) throw new Error('Fullscreen unavailable'); await this.panel.requestFullscreen(); }
      catch (_) { if (!this.disposed && this.view === '3d' && epoch === this.expansionEpoch) this.panel.classList.add('cl-three-view--expanded'); }
      if (this.disposed || this.view !== '3d' || epoch !== this.expansionEpoch) {
        if (global.document.fullscreenElement === this.panel) this.collapse();
        return;
      }
      this.expanding = false;
      this.expansionState(); this.canvas.focus(); this.engine?.resize();
    }
    collapse() {
      const expanded = this.expanding || global.document.fullscreenElement === this.panel || this.panel.classList.contains('cl-three-view--expanded');
      this.expansionEpoch++; this.expanding = false;
      if (global.document.fullscreenElement === this.panel) Promise.resolve(global.document.exitFullscreen()).catch(() => {});
      this.panel.classList.remove('cl-three-view--expanded'); this.expansionState(); this.engine?.resize();
      if (expanded && !this.disposed && this.view === '3d' && this.panel.contains(global.document.activeElement)) {
        const focus = this.root.contains(this.expansionFocus) && visible(this.expansionFocus) ? this.expansionFocus : this.thirdTab;
        focus.focus();
      }
      this.expansionFocus = null;
    }
    expansionState() {
      const expanded = global.document.fullscreenElement === this.panel || this.panel.classList.contains('cl-three-view--expanded');
      this.panel.dataset.clThreeExpanded = String(expanded);
      for (const button of this.root.querySelectorAll('[data-cl-three-action="expand"]')) button.setAttribute('aria-pressed', String(expanded));
      this.engine?.resize();
    }
    click(event) {
      const tab = event.target.closest('[data-clside-view], [data-cl-three-tab]');
      if (tab && this.tabs.includes(tab) && !tab.disabled) { this.setView(tab === this.thirdTab ? '3d' : tab.dataset.clsideView).catch(() => {}); return; }
      const button = event.target.closest('[data-cl-three-action], [data-cl-three-preset]');
      if (!button || !this.root.contains(button)) return;
      const action = button.dataset.clThreeAction;
      if (button.dataset.clThreePreset) { this.engine?.setView(button.dataset.clThreePreset); return; }
      if (action === 'retry') this.retry().catch(() => {});
      else if (action === 'reset') this.engine?.setView('front');
      else if (action === 'zoom-in') this.engine?.zoom(1.12);
      else if (action === 'zoom-out') this.engine?.zoom(1 / 1.12);
      else if (action === 'rotate-left') this.engine?.rotate(-.24);
      else if (action === 'rotate-right') this.engine?.rotate(.24);
      else if (action === 'expand') this.expand().catch(() => {});
      else if (action === 'close') this.collapse();
    }
    tabKey(event) {
      if (event.key === 'Escape' && this.view === 'side' && !event.altKey && !event.ctrlKey && !event.metaKey) {
        // Keep bubbling so the containing mobile preview can also close.
        event.preventDefault();
        this.setView('top').catch(() => {});
        this.tabs.find(tab => tab.dataset.clsideView === 'top')?.focus();
        return;
      }
      const tab = event.target.closest('[data-clside-view], [data-cl-three-tab]');
      if (!tab || !this.tabs.includes(tab) || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const tabs = this.tabs.filter(value => !value.disabled), index = tabs.indexOf(tab);
      const next = event.key === 'Home' ? tabs[0] : event.key === 'End' ? tabs.at(-1) : tabs[(index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      next.focus(); this.setView(next === this.thirdTab ? '3d' : next.dataset.clsideView).catch(() => {});
    }
    canvasKey(event) {
      if (event.target !== this.canvas || event.altKey || event.ctrlKey || event.metaKey || !this.active()) return;
      const keys = { ArrowLeft: () => this.engine?.rotate(-.18), ArrowRight: () => this.engine?.rotate(.18), ArrowUp: () => this.engine?.tilt(.10), ArrowDown: () => this.engine?.tilt(-.10) };
      if (keys[event.key]) { event.preventDefault(); keys[event.key](); }
      else if (event.key === 'Escape') { event.preventDefault(); this.collapse(); }
    }
    panelKey(event) {
      if (!this.active() || event.altKey || event.ctrlKey || event.metaKey) return;
      const expanded = this.panel.classList.contains('cl-three-view--expanded') || global.document.fullscreenElement === this.panel;
      if (!expanded) return;
      if (event.key === 'Escape') { event.preventDefault(); this.collapse(); }
      else if (event.key === 'Tab') {
        const controls = [...this.panel.querySelectorAll('button:not([disabled]), [tabindex="0"]')].filter(visible);
        const first = controls[0], last = controls.at(-1);
        if (first && event.shiftKey && (event.target === first || event.target === this.panel)) { event.preventDefault(); last.focus(); }
        else if (first && !event.shiftKey && event.target === last) { event.preventDefault(); first.focus(); }
      }
    }
    getDiagnostics() {
      return { revision, view: this.view, active: this.active(), state: this.desired && clone(this.desired), renderedState: this.rendered && clone(this.rendered),
        version: this.version, appliedVersion: this.appliedVersion, pending: Boolean(this.running || this.boot || this.timer), compositionPending: this.compositionPending, error: this.error,
        renderer: this.engine?.getDiagnostics() || null, runtime: this.prepared?.getDiagnostics() || null, illustrative: true, photorealismApproved: false };
    }
    dispose() {
      if (this.disposed) return;
      if (this.view === '3d') { this.photo(this.previousPhotoView); this.panel.hidden = true; }
      this.disposed = true; this.collapse(); this.cancelEpoch++; this.runId++; this.generation++; this.running = false; this.boot = null; clearTimeout(this.timer); this.timer = 0; clearTimeout(this.resizeTimer);
      this.abortController?.abort(); this.engine?.dispose(); this.prepared?.dispose(); this.preflight?.dispose(); this.listeners.splice(0).forEach(remove => remove());
      this.settle(abortError()); this.engine = null; this.prepared = null; this.preflight = null; instances.delete(this); delete this.root.clThreeView;
    }
  }

  function mount(root, options) { return root.clThreeView || new ThreeView(root, options); }
  function scan(scope) {
    const nodes = [...(scope.matches?.('[data-cl-three-view]') ? [scope] : []), ...scope.querySelectorAll('[data-cl-three-view]')];
    for (const root of nodes) {
      if (root.clThreeView) continue;
      try { mount(root); }
      catch (error) { root.dataset.clThreeStatus = 'error'; root.dataset.clThreeMountError = String(error.message || error); }
    }
  }
  const api = Object.freeze({ revision, snapshot, requiredResources, runtimeEntry, mount, scan, ThreeView });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else {
    global.CLThreeView = api;
    if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', () => scan(global.document), { once: true });
    else scan(global.document);
    global.document.addEventListener('shopify:section:load', event => scan(event.target));
    global.document.addEventListener('shopify:section:unload', event => { for (const instance of [...instances]) if (event.target.contains(instance.root)) instance.dispose(); });
  }
})(typeof window === 'undefined' ? globalThis : window);
